/**
 * What the app knows about updates, and who it tells (SPEC-010).
 *
 * Split from `appUpdate.js` because reporting and acting are two jobs: this
 * module holds the observable state and touches no browser API at all, while
 * `appUpdate.js` owns the service worker and the reload. Keeping the state here
 * is also what lets the React hook subscribe without importing any of that.
 */

import { VERSION_MANIFEST_URL, normalizeRuntimeVersion } from '../../domain/pwa/runtimeVersion';

/**
 * Substituted at build time by `DefinePlugin`. Absent in Jest, and absent in any
 * bundle built without the define — hence a fallback rather than a crash, since
 * an app that cannot name its own build should still run.
 */
export const currentRuntimeVersion = normalizeRuntimeVersion(
    typeof __APP_RUNTIME_VERSION__ === 'undefined' ? null : __APP_RUNTIME_VERSION__,
) || {
    appVersion: 'dev',
    buildId: 'dev',
    releaseChannel: 'development',
    manifestUrl: VERSION_MANIFEST_URL,
};

const defaultStatus = {
    isSupported: false,
    isChecking: false,
    isApplying: false,
    isUpdateReady: false,
    hasNewBuild: false,
    currentVersion: currentRuntimeVersion,
    latestVersion: null,
    error: null,
    phase: 'idle',
};

const listeners = new Set();

let currentStatus = { ...defaultStatus };

/**
 * One name for the whole state, so a caller never has to reason about which
 * combination of five booleans it is looking at.
 *
 * `ready` outranks `available` deliberately: a worker that has finished
 * installing can be handed over to instantly, while a manifest that merely
 * differs still needs the download.
 */
function derivePhase(status) {
    if (status.isApplying) {
        return 'applying';
    }

    if (status.isUpdateReady) {
        return 'ready';
    }

    if (status.hasNewBuild) {
        return 'available';
    }

    if (status.isChecking) {
        return 'checking';
    }

    if (status.error) {
        return 'failed';
    }

    return status.isSupported ? 'idle' : 'unsupported';
}

export function publishAppUpdateStatus(nextValues) {
    const nextStatus = { ...currentStatus, ...nextValues };

    nextStatus.phase = derivePhase(nextStatus);
    currentStatus = nextStatus;

    listeners.forEach((listener) => {
        listener(currentStatus);
    });

    return currentStatus;
}

export function getAppUpdateStatus() {
    return currentStatus;
}

export function subscribeAppUpdateStatus(listener) {
    listeners.add(listener);
    // Called immediately, because an update can be known before anything mounts:
    // the manifest check starts in `bootstrap.js`, before React renders.
    listener(currentStatus);

    return () => {
        listeners.delete(listener);
    };
}
